import { Card, CardContent } from '@/components/ui/card';
import { StarRating } from '@/components/ui/StarRating';
import { Testimonial } from '@/types/database';
import { Quote } from 'lucide-react';

interface TestimonialsProps {
  testimonials: Testimonial[];
  isLoading?: boolean;
}

export function Testimonials({ testimonials, isLoading }: TestimonialsProps) {
  // Demo testimonials if none in database
  const displayTestimonials = testimonials.length > 0 ? testimonials : [
    {
      id: '1',
      author_name: 'María García',
      author_avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
      content: 'Gracias al quiz encontré la silla ergonómica perfecta para mi home office. Después de 3 meses, el dolor lumbar que tenía ha desaparecido completamente.',
      rating: 5,
      is_active: true,
    },
    {
      id: '2',
      author_name: 'Carlos Rodríguez',
      author_avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
      content: 'El quiz me recomendó exactamente la silla que necesitaba. Las comparativas son muy detalladas y me ahorraron horas de investigación.',
      rating: 5,
      is_active: true,
    },
    {
      id: '3',
      author_name: 'Ana Martínez',
      author_avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
      content: 'Como fisioterapeuta, valoro las recomendaciones basadas en ergonomía real. Los análisis de cada silla son serios y bien documentados.',
      rating: 5,
      is_active: true,
    },
  ] as Testimonial[];

  if (isLoading) {
    return (
      <section className="bg-accent/50 py-16">
        <div className="container-wide">
          <div className="mb-10 text-center">
            <h2 className="font-display text-3xl font-bold">Lo Que Dicen Nuestros Lectores</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="mb-4 h-16 rounded bg-muted" />
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-muted" />
                    <div className="h-5 w-24 rounded bg-muted" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-accent/50 py-16">
      <div className="container-wide">
        {/* Header */}
        <div className="mb-10 text-center">
          <h2 className="font-display text-3xl font-bold">Lo Que Dicen Nuestros Lectores</h2>
          <p className="mt-2 text-muted-foreground">
            Miles de profesionales confían en nuestras recomendaciones
          </p>
        </div>

        {/* Grid */}
        <div className="grid gap-6 md:grid-cols-3">
          {displayTestimonials.slice(0, 3).map((testimonial) => (
            <Card key={testimonial.id} className="relative border-0 bg-card shadow-lg">
              {/* Quote Icon */}
              <div className="absolute -top-3 left-6">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
                  <Quote className="h-4 w-4 text-primary-foreground" />
                </div>
              </div>

              <CardContent className="p-6 pt-8">
                {/* Rating */}
                <StarRating rating={testimonial.rating} size="sm" className="mb-4" />

                {/* Content */}
                <p className="mb-6 text-muted-foreground">
                  "{testimonial.content}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <img
                    src={testimonial.author_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.author_name)}&background=random`}
                    alt={testimonial.author_name}
                    className="h-12 w-12 rounded-full object-cover"
                    loading="lazy"
                  />
                  <div>
                    <p className="font-semibold">{testimonial.author_name}</p>
                    <p className="text-sm text-muted-foreground">Usuario verificado</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Disclaimer */}
        <p className="mt-8 text-center text-xs text-muted-foreground">
          * Los testimonios mostrados son ejemplos ilustrativos. Los resultados pueden variar según cada persona.
        </p>
      </div>
    </section>
  );
}